import { Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx';
import type { TFunction } from 'i18next';
import { normalizeId } from './id';
import { getLocalizedText } from './i18n';
import { saveBlobAsFile } from './download';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export type ExportCourseNotesResult = { ok: true } | { ok: false; reason: 'empty' };

function sanitizeFilename(name: string): string {
  const cleaned = name.replace(/[/\\:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
  const base = cleaned.replace(/\.docx$/i, '');
  return `${base || 'notes'}.docx`;
}

/** Texte localisé non vide, ou chaîne vide */
function localizedNonEmpty(field: any): string {
  if (field === undefined || field === null) return '';
  const s = getLocalizedText(field).trim();
  return s;
}

/** « partie libellé » : titre du chapitre si description/summary ; sinon vide */
function pickChapterLabelPart(chapter: any): string {
  for (const field of [chapter?.description, chapter?.summary]) {
    const s = localizedNonEmpty(field);
    if (s) return s;
  }
  return '';
}

/** Libellé court pour la capsule : label métier, sinon description, sinon summary */
function pickCapsuleLabelPart(item: any): string {
  for (const field of [item?.label, item?.description, item?.summary]) {
    const s = localizedNonEmpty(field);
    if (s) return s;
  }
  return '';
}

/** Affiche « libellé : titre » si un libellé existe, sinon seulement le titre */
function formatLabelColonTitle(labelPart: string, titleText: string): string {
  const title = titleText.trim();
  const label = labelPart.trim();
  if (label) return `${label} : ${title}`;
  return title;
}

export async function exportCourseNotesDocx(params: {
  course: any;
  notes: any[];
  firstName: string;
  lastName: string;
  t: TFunction;
}): Promise<ExportCourseNotesResult> {
  const { course, notes, firstName, lastName, t } = params;

  const withContent = (Array.isArray(notes) ? notes : []).filter(
    (n) => n?.content && String(n.content).trim().length > 0
  );
  const chapterIdSet = new Set(
    (course.chapters || []).map((ch: any) => normalizeId(ch._id))
  );
  const inCourse = withContent.filter((n) => chapterIdSet.has(normalizeId(n.chapter)));
  if (inCourse.length === 0) {
    return { ok: false, reason: 'empty' };
  }

  const children: Paragraph[] = [];

  children.push(
    new Paragraph({
      text: getLocalizedText(course.title),
      heading: HeadingLevel.TITLE,
      spacing: { after: 400 },
    })
  );

  for (const chapter of course.chapters || []) {
    const chapterId = normalizeId(chapter._id);
    const chapterNotes = inCourse.filter((n) => normalizeId(n.chapter) === chapterId);
    if (chapterNotes.length === 0) continue;

    const chapterTitleText = getLocalizedText(chapter.title);
    const chapterLine = formatLabelColonTitle(pickChapterLabelPart(chapter), chapterTitleText);

    const capsuleItems = (chapter.items || []).filter((it: any) => it.itemType === 'capsule');
    const shownNoteIds = new Set<string>();

    const emitBlock = (capsuleItem: any | null, content: string) => {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: chapterLine })],
          spacing: { after: 120 },
        })
      );

      const capsuleLine = capsuleItem
        ? formatLabelColonTitle(
            pickCapsuleLabelPart(capsuleItem),
            getLocalizedText(capsuleItem.title)
          )
        : t('notesExport.unknownCapsule');

      children.push(
        new Paragraph({
          children: [new TextRun({ text: capsuleLine })],
          spacing: { after: 200 },
        })
      );

      const lines = String(content).split(/\r?\n/);
      for (const line of lines) {
        children.push(
          new Paragraph({
            text: line,
            spacing: { after: 80 },
          })
        );
      }
      children.push(new Paragraph({ text: '', spacing: { after: 400 } }));
    };

    for (const item of capsuleItems) {
      const capId = normalizeId(item._id || item.id);
      const note = chapterNotes.find((n) => normalizeId(n.capsuleId) === capId);
      if (!note) continue;
      shownNoteIds.add(normalizeId(note._id));
      emitBlock(item, String(note.content));
    }

    for (const note of chapterNotes) {
      if (shownNoteIds.has(normalizeId(note._id))) continue;
      emitBlock(null, String(note.content));
    }
  }

  const doc = new Document({
    sections: [
      {
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const rawFilename = t('notesExport.docFilename', {
    courseTitle: getLocalizedText(course.title),
    firstName: firstName.trim(),
    lastName: lastName.trim(),
  });
  await saveBlobAsFile(blob, sanitizeFilename(rawFilename), DOCX_MIME);

  return { ok: true };
}
